/*
  Say The Label, product page (slice 4a). Loaded by blocks/stl-pd-data.liquid on product templates.

  The commerce plumbing on this page is the THEME'S: <variant-picker>, <product-form-component>
  with input[name="id"], <add-to-cart-component>, <product-price>, <media-gallery>. This file never
  replaces any of it. It reads the page, listens to the theme's own events and adds the mock's
  behaviour around them:
    1. AU / US / EU tabs that only RELABEL the theme's size buttons (storage key stl_size_tab)
    2. the stock line, the Afterpay instalment and the sticky bars, kept in step with the variant
    3. the sticky bars follow the theme's own button label ("Add to bag" in English, the theme's
       translation elsewhere)
    4. Find my size (with the mock's gap bug fixed) in the shared modal #stl-modal
    5. the Size guide and Afterpay triggers (#stl-sizeModal and #stl-apDrawer are slice 4b's;
       each has a plain fallback here so the link is never dead)
    6. story bubbles: a clip loads only while its bubble is on screen (IntersectionObserver)
    7. the daisy and the model line placed over photo one, and the n / total pill on phones
    8. the sticky bars (#stl-stickyAtc, #stl-topbar), which press the theme's real add button
  Money is always money() from @stl/core. Nothing throws when a piece is missing: a gift card or a
  product with no style simply has fewer pieces.

  THE THEME EDITOR. Changing a setting in Customize makes Shopify render this section again and
  REPLACE its DOM, then fire `shopify:section:load` on the new wrapper. Everything here binds to
  elements and to the document, so the fresh markup carried no listeners: the AU / US / EU tabs
  stopped converting, silently, with no error (build-recon/night-editor-speed-notes.md section 1).
  So every listener, observer and interval below takes an AbortSignal, `wireProduct` is the one
  entry point, and a re-render aborts the old signal before wiring the new node: one bind per node,
  never two, and no document listener left over from the node that has gone. The idiom is
  `assets/stl-ask.js`'s. It is inert on the live storefront, where nothing re-renders a section.
*/
import * as core from '@stl/core';

const { onReady, delegate, money, storage, wishlist } = core;
const ON = 'stl-is-on';
const TAB_KEY = 'stl_size_tab';
const LETTERS = ['2XS', 'XS', 'S', 'M', 'L', 'XL', '2XL'];

/* The Find my size inputs carry these bounds. The form is novalidate, so run() enforces them too:
   a bust of -5 or 999 is treated as not entered rather than answered with a size. */
const CM_MIN = 40;
const CM_MAX = 200;

/* A size VALUE can be a band: the Flora Wrap Skirt sells "XS - S", "M - L" and "XL - 2XL", so one
   variant answers to every letter in it. Every comparison of a sold size against a size-guide row
   goes through this. A plain value comes back as itself, cleaned, and anything that is not a run
   of size letters (a colour, "One Size") is never split. */
const SIZE_TOKEN = /^(?:[0-9]*X{0,3}(?:S|M|L)|[0-9]+)$/;

function sizeTokens(value) {
  const clean = (part) => String(part).toUpperCase().replace(/[^A-Z0-9]/g, '');
  const whole = clean(value == null ? '' : value);
  const parts = String(value == null ? '' : value)
    .split(/\s*[-\u2013\u2014\/,]\s*/)
    .map(clean)
    .filter(Boolean);
  if (parts.length > 1 && parts.every((p) => SIZE_TOKEN.test(p))) return parts;
  return whole ? [whole] : [];
}

const $ = (sel, root = document) => root.querySelector(sel);
const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));

/* A MutationObserver that stops when the section it watches is re-wired or thrown away. Every
   observer on this page watches markup the theme editor replaces, so none of them may outlive it. */
function observe(target, options, signal, run) {
  if (!target || !('MutationObserver' in window)) return;
  const mo = new MutationObserver(run);
  mo.observe(target, options);
  if (signal) signal.addEventListener('abort', () => mo.disconnect(), { once: true });
}

function readJSON(el) {
  if (!el) return null;
  try {
    return JSON.parse(el.textContent || 'null');
  } catch (e) {
    return null;
  }
}

function escapeHTML(value) {
  return String(value == null ? '' : value).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}

function fillWords(template, values) {
  return String(template || '').replace(/\[(\w+)\]/g, (m, key) => (key in values ? values[key] : m));
}

/* ---------------------------------------------------------------- state */
const state = {
  root: null,
  cfg: null,
  style: null,
  colourway: null,
  facts: null,
  tab: 'au',
  variantId: null,
  /* false until the shopper taps a size (see initSizeGate) */
  sizeChosen: true,
  /* times the main button's label was put back to "Add to bag" on this page (see paintPreGate) */
  preHeldWrites: 0,
};

function sizesOf() {
  return (state.colourway && state.colourway.sizes) || [];
}

function currentVariantId() {
  const input = $('product-form-component form input[name="id"]') || $('form[data-type="add-to-cart-form"] input[name="id"]');
  if (input && input.value) return String(input.value);
  const checked = $('variant-picker input:checked[data-variant-id]');
  if (checked) return String(checked.dataset.variantId);
  return state.variantId;
}

function currentSize() {
  if (!state.sizeChosen) return null;
  const id = currentVariantId();
  return sizesOf().find((z) => String(z.id) === String(id)) || null;
}

/* ---------------------------------------------------------------- 1. size tabs */
/* AU counts up from the anchor (XS = AU n) in steps of 2; US = AU - 4; EU = AU + 28. A banded
   value ("XS - S") converts each end. Anything that is not one of the seven letters is left alone. */
function convertOne(letter, tab) {
  const i = LETTERS.indexOf(letter);
  if (i === -1) return null;
  const au = state.cfg.sizeAnchor + (i - 1) * 2;
  if (tab === 'us') return au - 4;
  if (tab === 'eu') return au + 28;
  return au;
}

function sizeLabel(value, tab) {
  if (tab === 'au' || !value) return value;
  const parts = String(value).split(/\s*-\s*/).map((p) => p.trim().toUpperCase());
  const out = parts.map((p) => convertOne(p, tab));
  if (out.some((n) => n === null)) return value;
  return out.join('-');
}

function relabelSizes() {
  const box = $('[data-stl-pd-sizes]');
  if (!box || !state.cfg.hasSize) return;
  box.setAttribute('data-stl-size-tab', state.tab);
  $$('[data-stl-tab]', box).forEach((b) => {
    const on = b.dataset.stlTab === state.tab;
    b.classList.toggle(ON, on);
    b.setAttribute('aria-pressed', on ? 'true' : 'false');
  });
  $$('variant-picker fieldset', box).forEach((fieldset) => {
    const legend = $('legend', fieldset);
    const name = legend ? (legend.firstChild && legend.firstChild.textContent ? legend.firstChild.textContent : legend.textContent).trim().toLowerCase() : '';
    if (name && name !== 'size') return;
    $$('label', fieldset).forEach((label) => {
      const input = $('input', label);
      const text = $('.variant-option__button-label__text', label);
      if (!input || !text) return;
      const wanted = sizeLabel(input.value, state.tab);
      if (text.textContent !== wanted) text.textContent = wanted;
      label.setAttribute('data-stl-size', input.value);
      /* The theme writes the accessible name from the AU value. Once the button on screen reads
         "4", a screen reader must hear "S, US 4", not a size the shopper cannot see. The theme's
         own name is captured first so the sold-out tail it appends survives, and a morph hands
         back a fresh input, so it is captured again. */
      if (input.dataset.stlAria == null) input.dataset.stlAria = input.getAttribute('aria-label') || input.value;
      const named = input.dataset.stlAria;
      const tail = named.indexOf(input.value) === 0 ? named.slice(input.value.length) : '';
      const spoken = wanted === input.value ? input.value : input.value + ', ' + state.tab.toUpperCase() + ' ' + wanted;
      if (input.getAttribute('aria-label') !== spoken + tail) input.setAttribute('aria-label', spoken + tail);
    });
  });
}

/* A product with no Size option and several variants of one kind (the gift card's denominations)
   is laid out cheapest first. CSS `order` alone moves only the pixels: keyboard focus still runs
   in Shopify's order, so it jumps $500 to $125 to $60. The same price order, emitted once by
   blocks/stl-pd-sizes.liquid, is applied to the DOM here. The CSS stays as the no-JavaScript
   fallback; with this run the two agree. Idempotent, and run again after the theme's morph. */
function sortPlainOptions() {
  const box = $('[data-stl-pd-sizes]');
  if (!box || state.cfg.hasSize) return;
  const order = readJSON($('[data-stl-price-order]', box));
  if (!Array.isArray(order) || order.length < 2) return;
  $$('variant-picker fieldset', box).forEach((fieldset) => {
    const labels = $$('label', fieldset);
    if (labels.length < 2) return;
    const parent = labels[0].parentNode;
    if (labels.some((label) => label.parentNode !== parent)) return;
    const rank = (label) => {
      const input = $('input', label);
      const at = input ? order.indexOf(Number(input.dataset.variantId)) : -1;
      return at === -1 ? order.length : at;
    };
    const wanted = labels.slice().sort((a, b) => rank(a) - rank(b));
    /* appendChild writes a mutation record even when the node lands back where it was, and the
       picker's own MutationObserver calls this function, so moving anything unconditionally is an
       endless loop. Move nothing when the order is already right. */
    if (wanted.every((label, i) => label === labels[i])) return;
    wanted.forEach((label) => parent.appendChild(label));
  });
}

function initTabs(signal) {
  const box = $('[data-stl-pd-sizes]');
  if (!box) return;
  /* the block sets data-stl-size-tab from the shopper's market (us on /en-us), a saved choice wins */
  const dflt = box.getAttribute('data-stl-size-tab') === 'us' ? 'us' : 'au';
  const saved = storage.get(TAB_KEY, dflt);
  state.tab = saved === 'us' || saved === 'eu' ? saved : dflt;
  relabelSizes();
  delegate(
    box,
    'click',
    '[data-stl-tab]',
    (event, button) => {
      state.tab = button.dataset.stlTab;
      storage.set(TAB_KEY, state.tab);
      relabelSizes();
    },
    { signal }
  );
  /* the theme morphs the picker after every choice, which writes the letters back */
  observe($('variant-picker', box), { childList: true, subtree: true, characterData: true }, signal, () => {
    relabelSizes();
    sortPlainOptions();
  });
}

/* ---------------------------------------------------------------- 2. stock line, Afterpay, labels */
function stoqButton() {
  /* STOQ (Restock Rocket) puts its waitlist inside <product-form-component>, after
     <add-to-cart-component>: an inline form on this store, a NOTIFY ME button on others. */
  const el = $('#restock-rocket-inline-container, .restock-rocket-wrapper-inline, product-form-component [class*="restock-rocket"], .restock-rocket-button');
  if (!el) return null;
  const box = el.getBoundingClientRect();
  return box.width > 0 && box.height > 0 ? el : null;
}

function openWaitlist() {
  const el = stoqButton();
  if (!el) {
    $('[data-stl-pd-sizes]')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    return;
  }
  if (el.matches('button, a, [role="button"]')) {
    el.click();
    return;
  }
  el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  const field = el.querySelector('input:not([type="hidden"]), button');
  if (field) setTimeout(() => field.focus({ preventScroll: true }), 400);
}

function paintStockLine() {
  const line = $('[data-stl-stockline]');
  if (!line || !state.colourway) return;
  if (!state.sizeChosen && line.hasAttribute('data-stl-asked')) return;
  const words = state.cfg.words;
  const sizes = sizesOf();
  const z = currentSize();
  const dot = (out) => '<span class="stl-dot' + (out ? ' stl-dot--out' : '') + '"></span>';
  let html = '';
  if (sizes.length && sizes.every((s) => !s.available)) {
    html = dot(true) + escapeHTML(state.cfg.hasSize ? words.allOut : words.allOutNoSize || words.allOut);
  } else if (z && !z.available) {
    html = dot(true) + escapeHTML(state.cfg.hasSize ? fillWords(words.sizeOut, { size: z.value }) : words.allOutNoSize || words.allOut);
    if (stoqButton()) html += ' <button type="button" data-stl-waitlist>' + escapeHTML(words.waitlist) + '</button>';
  } else if (z && z.q > 0 && z.q <= state.cfg.lowStock) {
    const template = state.cfg.hasSize ? words.lowStock : words.lowStockNoSize;
    html = dot(false) + escapeHTML(fillWords(template, { count: z.q, size: z.value }));
  }
  if (line.innerHTML !== html) line.innerHTML = html;
}

function paintAfterpay() {
  const amount = $('[data-stl-ap-amount]');
  const z = currentSize();
  if (!amount || !z) return;
  amount.textContent = money(Math.round(z.price / 4));
}

/* The theme's own button says "Add to bag": the locale string products.product.add_to_cart was
   changed by the conductor on 22 Sep 2026 (decision D20), so the JavaScript relabel this file used
   to carry is gone. On /it and /zh the theme's translated label stands, as it always did. */
function watchAddButton(signal) {
  const host = $('[data-stl-atc-row]') || $('product-form-component');
  observe(host, { childList: true, subtree: true, characterData: true }, signal, () => {
    paintStockLine();
    /* STOQ relabels the main button late: the bars follow it, on a pre-order size only (or, before
       a size is picked, on a colourway that has one). Before a size is picked on a colourway that
       also has sizes in stock, the main button is put back to "Add to bag" first (paintPreGate). */
    const z = currentSize();
    if (!z && hasPreSize()) paintPreGate();
    if (z ? z.pre : hasPreSize()) paintSticky();
  });
}

/* The sticky bars must say exactly what the main button says. In English the theme's own locale
   string is now "Add to bag" (D20), so the bar's own setting and the theme agree; in every other
   locale the theme's translated label wins, so the bars take it rather than dropping to English. */
function stickyLabel(button, out) {
  const theme = out ? state.cfg.themeSoldOutLabel : state.cfg.themeAddLabel;
  const english = out ? 'Sold out' : 'Add to cart';
  const own = out ? button.dataset.stlLabelOut || button.dataset.stlLabelAdd : button.dataset.stlLabelAdd;
  if (theme && theme !== english) return theme;
  return own;
}

/* A pre-order size (27 Oct 2026; "pre" in the style data, snippets/stl-style.liquid): STOQ rewrites
   the MAIN button's words ("Pre-order") a moment after the size changes, and the bars only press
   that button, so they say what it says. Until STOQ has written it, "Pre-order", never the
   theme's "Add to bag". */
function mainAddButton() {
  return $('[data-stl-atc-row] add-to-cart-component button') || $('add-to-cart-component button');
}

/* The main button's words when STOQ (or the theme, for a pre-order size: blocks/buy-buttons.liquid)
   has written something other than the plain add label, else ''. */
function mainPreWords(button) {
  const main = mainAddButton();
  const words = main && (main.querySelector('.add-to-cart-text__content') || main);
  const text = words ? (words.textContent || '').replace(/\s+/g, ' ').trim() : '';
  const plain = [state.cfg.themeAddLabel, button.dataset.stlLabelAdd, 'Add to bag', 'Add to cart']
    .filter(Boolean)
    .map((t) => String(t).trim().toLowerCase());
  return text && plain.indexOf(text.toLowerCase()) === -1 ? text : '';
}

function preLabel(button) {
  return mainPreWords(button) || 'Pre-order';
}

/* The current colourway has a pre-order size. Before a size is picked STOQ labels the main button
   "Pre-order" on such a colourway, and the bars said "Add to bag" beside it (27 Oct 2026). */
function hasPreSize() {
  return !!(state.colourway && Array.isArray(state.colourway.sizes) && state.colourway.sizes.some((z) => z.available && z.pre));
}

/* Every size she can buy in this colourway is a pre-order (the TEST product, or a colourway that
   has sold through and gone to pre-order whole). Only then does the button say "Pre-order" before
   a size is picked. */
function allPre() {
  const sizes = state.colourway && Array.isArray(state.colourway.sizes) ? state.colourway.sizes.filter((z) => z.available) : [];
  return sizes.length > 0 && sizes.every((z) => z.pre);
}

/* A MIXED colourway with nothing picked yet (audit T1, 29 Oct 2026). Shopify's hidden id is the
   first size it can sell, and with 2XS first in the size order and on pre-order, STOQ and the
   theme both read that id and labelled the whole colourway "Pre-order" while XS, L and XL sat in
   stock (Wave Denim Jacket, Wrap Top Blush Pink). Until she taps a size it is "Add to bag" and
   STOQ's ship-date box stays hidden; the hidden input[name=id] is left exactly where it is,
   because STOQ reads it. The size gate (initSizeGate) still stops any add before a size is picked. */
function preUnpicked() {
  return !state.sizeChosen && hasPreSize() && !allPre();
}

/** The main button's visible words, as text nodes (never the icon or the "added" burst). */
function labelTextNodes(main) {
  const scope = main.querySelector('.add-to-cart-text__content') || main;
  const walker = document.createTreeWalker(scope, NodeFilter.SHOW_TEXT, {
    acceptNode: (node) => {
      const parent = node.parentElement;
      if (!parent || parent.closest('svg, .add-to-cart__added, .add-to-cart-icon')) return NodeFilter.FILTER_REJECT;
      return node.data.trim() ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_SKIP;
    },
  });
  const nodes = [];
  while (walker.nextNode()) nodes.push(walker.currentNode);
  return nodes;
}

/* STOQ writes "Pre-order" on the main button after load; this puts the plain label back while
   preUnpicked() holds. Capped, so an app that fights back cannot loop with us: past the cap
   STOQ's word stands, which is how the page behaved before. */
const PRE_HELD_CAP = 40;

function paintPreGate(relabel = true) {
  const held = preUnpicked();
  document.documentElement.toggleAttribute('data-stl-pre-unpicked', held);
  $$('[data-stl-atc-row]').forEach((row) => row.toggleAttribute('data-stl-pre-unpicked', held));
  const main = mainAddButton();
  if (!main) return;
  const plain = state.cfg.themeAddLabel || 'Add to bag';
  if (held) {
    const nodes = labelTextNodes(main);
    const text = nodes.map((n) => n.data).join('').replace(/\s+/g, ' ').trim();
    if (!nodes.length || text === plain || state.preHeldWrites >= PRE_HELD_CAP) return;
    state.preHeldWrites += 1;
    nodes[0].data = plain;
    nodes.slice(1).forEach((n) => (n.data = ''));
    main.setAttribute('data-stl-pre-held', String(state.preHeldWrites));
    return;
  }
  /* she has tapped a size: if it is a pre-order and nothing has relabelled the button since, say so.
     Not from the tap itself (relabel false): the hidden id still names the old size until the
     theme's variant:update, so this waits for onVariantChange. */
  if (!relabel || !main.hasAttribute('data-stl-pre-held')) return;
  main.removeAttribute('data-stl-pre-held');
  const z = currentSize();
  const nodes = labelTextNodes(main);
  const text = nodes.map((n) => n.data).join('').replace(/\s+/g, ' ').trim();
  if (z && z.pre && z.available && nodes.length && text === plain) {
    nodes[0].data = 'Pre-order';
    nodes.slice(1).forEach((n) => (n.data = ''));
  }
}

/* The delivery and returns line under Add to bag (audit T7, 29 Oct 2026; blocks/stl-pd-atc.liquid,
   only printed while settings.stl_pd_reassure_line is on). It shows on a size in stock and hides on
   a pre-order or sold-out size, where STOQ's ship-date box or the waitlist speaks instead. With
   nothing picked it shows while the colourway has a size in stock. No style data: Liquid's first
   paint stands. */
function paintReassure() {
  const line = $('[data-stl-reassure]');
  if (!line || !state.colourway) return;
  const z = currentSize();
  const show = z ? z.available && !z.pre : sizesOf().some((s) => s.available && !s.pre);
  if (line.hidden === show) line.hidden = !show;
}

function paintSticky() {
  const z = currentSize();
  const showSize = state.cfg.hasSize && z;
  const price = z ? z.price : state.colourway ? state.colourway.price : null;
  $$('[data-stl-sticky-meta]').forEach((el) => {
    if (price == null) return;
    el.textContent = showSize ? z.value + ' · ' + money(price) : money(price);
  });
  $$('[data-stl-sticky-add]').forEach((button) => {
    const out = z ? !z.available : state.colourway ? !state.colourway.available : false;
    let label = stickyLabel(button, out);
    if (!out && z && z.pre) label = preLabel(button);
    /* nothing picked: "Pre-order" only when every size she can buy is one (allPre); a mixed
       colourway keeps the plain label, as the main button does (paintPreGate) */
    else if (!out && !z && allPre()) label = preLabel(button);
    /* past the cap STOQ's word stands on the main button, so the bars say it too */
    else if (!out && !z && hasPreSize() && state.preHeldWrites >= PRE_HELD_CAP) label = mainPreWords(button) || label;
    /* a sold-out size with STOQ's waitlist on the page: the bar takes her to the form (F90) */
    if (out && button.dataset.stlLabelWait && stoqButton()) label = button.dataset.stlLabelWait;
    button.textContent = label;
    button.dataset.stlMode = out ? 'wait' : 'add';
  });
}

function onVariantChange() {
  state.variantId = currentVariantId();
  relabelSizes();
  paintStockLine();
  paintAfterpay();
  paintPreGate();
  paintSticky();
  /* STOQ puts its waitlist back a moment after the size changes; label the bars again then (F90) */
  clearTimeout(state.stickyAgain);
  state.stickyAgain = setTimeout(paintSticky, 1200);
  paintReassure();
}

/* ---------------------------------------------------------------- 3b. no size until she picks one */
/* Shopify's picker arrives with the first size in stock already ticked, so Add to bag tapped
   without a choice added that size (10 Oct 2026: Daisy Powder Blue opened on XS with one left,
   punch list 27). Unless the link names a variant, the ticks come off on load and Add to bag, the
   sticky bars included (they click the main button), asks for a size instead of adding. */
const sizeInputs = () => $$('[data-stl-pd-sizes] variant-picker input[type="radio"]');

function askForSize() {
  const line = $('[data-stl-stockline]');
  if (line) {
    line.setAttribute('data-stl-asked', '');
    line.innerHTML = '<span class="stl-dot"></span>' + escapeHTML(state.cfg.words.pickSize || 'Please select a size');
  }
  const sizes = $('[data-stl-pd-sizes]');
  if (sizes) {
    const box = sizes.getBoundingClientRect();
    if (box.top < 80 || box.bottom > window.innerHeight) sizes.scrollIntoView({ behavior: 'smooth', block: 'center' });
    sizes.classList.remove('stl-pd-sizes--ask');
    void sizes.offsetWidth;
    sizes.classList.add('stl-pd-sizes--ask');
  }
}

function initSizeGate(signal) {
  const inputs = sizeInputs();
  const named = new URLSearchParams(window.location.search).has('variant');
  state.sizeChosen = !state.cfg.hasSize || inputs.length < 2 || named;
  if (state.sizeChosen) return;
  inputs.forEach((input) => {
    input.checked = false;
  });
  $('[data-stl-stockline]')?.replaceChildren();

  document.addEventListener(
    'change',
    (event) => {
      if (!(event.target instanceof HTMLInputElement) || !event.target.closest('[data-stl-pd-sizes] variant-picker')) return;
      state.sizeChosen = true;
      $('[data-stl-stockline]')?.removeAttribute('data-stl-asked');
      $('[data-stl-pd-sizes]')?.classList.remove('stl-pd-sizes--ask');
      /* STOQ's ship-date box may show from here (paintPreGate clears the hold) */
      paintPreGate(false);
      paintReassure();
    },
    { capture: true, signal }
  );
  const stop = (event) => {
    if (state.sizeChosen) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    askForSize();
  };
  document.addEventListener(
    'click',
    (event) => {
      if (event.target instanceof Element && event.target.closest('add-to-cart-component button')) stop(event);
    },
    { capture: true, signal }
  );
  document.addEventListener(
    'submit',
    (event) => {
      if (event.target instanceof Element && event.target.closest('product-form-component')) stop(event);
    },
    { capture: true, signal }
  );
}

/* ---------------------------------------------------------------- 4. Find my size */
function parseStd(text) {
  const out = { bust: {}, waist: {} };
  String(text || '')
    .split(/\n+/)
    .forEach((row) => {
      const m = row.match(/^\s*(bust|waist)\s*:\s*(.+)$/i);
      if (!m) return;
      m[2].split(',').forEach((part) => {
        const p = part.trim().match(/^([0-9A-Za-z]+)\s+(\d+)\s*[-–]\s*(\d+)$/);
        if (p) out[m[1].toLowerCase()][p[1].toUpperCase()] = [Number(p[2]), Number(p[3])];
      });
    });
  return out;
}

/* Reads the style's own measurement page: the header row names the BUST and WAIST columns, every
   later row is a size with "80-85" style bands (an en dash counts). */
function parseGuide() {
  const src = $('template[data-stl-size-guide-src]');
  if (!src) return null;
  const scope = src.content.querySelector('[data-stl-size-guide="cm"]') || src.content;
  const out = { bust: {}, waist: {} };
  let found = false;
  $$('table', scope).forEach((table) => {
    const rows = $$('tr', table);
    if (rows.length < 2) return;
    const head = $$('th, td', rows[0]).map((c) => c.textContent.trim().toUpperCase());
    const bustCol = head.findIndex((h) => h.includes('BUST'));
    const waistCol = head.findIndex((h) => h.includes('WAIST'));
    if (bustCol === -1 && waistCol === -1) return;
    rows.slice(1).forEach((row) => {
      const cells = $$('th, td', row).map((c) => c.textContent.trim());
      const tokens = sizeTokens(cells[0] || '');
      if (!tokens.length) return;
      [
        ['bust', bustCol],
        ['waist', waistCol],
      ].forEach(([key, col]) => {
        if (col === -1 || !cells[col]) return;
        const m = cells[col].match(/(\d+(?:\.\d+)?)\s*[-–]\s*(\d+(?:\.\d+)?)/);
        if (m) {
          tokens.forEach((token) => {
            out[key][token] = [Number(m[1]), Number(m[2])];
          });
          found = true;
        }
      });
    });
  });
  return found ? out : null;
}

/* The mock returned nothing when a measurement fell in a gap between two bands (the Peggy guide
   runs S 85-89 then M 92-97, so a 90 or 91cm bust got no answer). Fixed: no band contains it, so
   the band whose middle is nearest wins, and the note says so. */
function suggestSize(bust, waist, hips) {
  const isBottom = state.style && state.style.category === 'bottoms';
  const key = isBottom ? 'waist' : 'bust';
  const value = isBottom ? waist : bust;
  if (!value || Number.isNaN(value)) return { key, size: null };
  const guide = parseGuide();
  const words = readJSON($('[data-stl-pd-fit-words]')) || {};
  const std = parseStd(words.std);
  let table = guide && Object.keys(guide[key]).length ? guide[key] : null;
  let from = 'this piece’s size guide';
  if (!table) {
    table = std[key];
    from = 'the Say standard chart';
  }
  const mine = [];
  sizesOf().forEach((z) => {
    sizeTokens(z.value).forEach((token) => {
      if (mine.indexOf(token) === -1) mine.push(token);
    });
  });
  let bands = Object.keys(table).map((size) => ({ size, lo: table[size][0], hi: table[size][1] }));
  const sold = bands.filter((b) => mine.includes(b.size));
  if (sold.length) bands = sold;
  if (!bands.length) return { key, size: null };
  bands.sort((a, b) => a.lo - b.lo);
  let pick = bands.find((b) => value >= b.lo && value <= b.hi);
  let nearest = false;
  if (!pick) {
    if (value < bands[0].lo) pick = bands[0];
    else if (value > bands[bands.length - 1].hi) pick = bands[bands.length - 1];
    else {
      nearest = true;
      pick = bands.reduce((best, b) => (Math.abs((b.lo + b.hi) / 2 - value) < Math.abs((best.lo + best.hi) / 2 - value) ? b : best), bands[0]);
    }
  }
  let note = 'Based on your ' + key + ' of ' + value + ' cm against ' + from + '.';
  if (nearest) note += ' You sit between two sizes on this guide, so this is the closer one.';
  const fit = (state.style && state.style.fit) || '';
  if (state.style && state.style.key === 'wave-denim-dress' && bust && hips && hips - bust >= 8) {
    note += ' This dress is slim through the hips: with hips a size up from your bust, take the larger size.';
  }
  if (fit === 'Runs small' || fit === 'Slightly small') note += ' This style runs small, so go up if you are near the top of the range.';
  if (fit === 'Runs large' || fit === 'Slightly large') note += ' This style runs a little large, so go down if you are near the bottom of the range.';
  const match = sizesOf().find((z) => sizeTokens(z.value).indexOf(pick.size) !== -1);
  return { key, size: match ? match.value : pick.size, record: match || null, note };
}

function listOf(names) {
  if (names.length < 2) return names.join('');
  return names.slice(0, -1).join(', ') + ' and ' + names[names.length - 1];
}

function openFit(trigger) {
  const words = readJSON($('[data-stl-pd-fit-words]')) || {};
  const node = document.createElement('div');
  node.className = 'stl-fit';
  node.innerHTML =
    '<p class="stl-lead">' + escapeHTML(words.lead) + '</p>' +
    '<form class="stl-fitform" novalidate>' +
    /* aria-describedby points every field at #stl-faOut, which is the live region the answer and
       any "check the bust" message are written into, so the field that is wrong and the sentence
       saying why are one thing to a screen reader instead of two unrelated announcements. */
    '<label>Bust<input type="number" inputmode="decimal" min="' + CM_MIN + '" max="' + CM_MAX + '" name="bust" placeholder="88" aria-describedby="stl-faOut" data-stl-autofocus></label>' +
    '<label>Waist<input type="number" inputmode="decimal" min="' + CM_MIN + '" max="' + CM_MAX + '" name="waist" placeholder="70" aria-describedby="stl-faOut"></label>' +
    '<label>Hips<input type="number" inputmode="decimal" min="' + CM_MIN + '" max="' + CM_MAX + '" name="hips" placeholder="96" aria-describedby="stl-faOut"></label>' +
    '</form>' +
    '<button type="button" class="stl-btn" id="stl-faGo">' + escapeHTML(words.go) + '</button>' +
    '<div id="stl-faOut" aria-live="polite"></div>' +
    '<p class="stl-fine">' + escapeHTML(words.fine) + '</p>';
  const form = $('form', node);
  const out = $('#stl-faOut', node);
  /* Outside CM_MIN to CM_MAX the number is not a body measurement, so it counts as not entered
     and the shopper gets the prompt back instead of a confident nonsense answer. */
  const reading = (raw) => {
    const n = parseFloat(raw);
    return Number.isFinite(n) && n >= CM_MIN && n <= CM_MAX ? n : NaN;
  };
  /* "Not a body measurement" and "not filled in yet" read the same to reading(), but they are not
     the same to the shopper: typing 999 and being told to "pop in your bust" looks like the form
     ignored them. So the out-of-range case says what is wrong and what the range is. */
  const outOfRange = (raw) => {
    const n = parseFloat(String(raw).trim());
    return String(raw).trim() !== '' && Number.isFinite(n) && (n < CM_MIN || n > CM_MAX);
  };
  const run = () => {
    const bust = reading(form.bust.value);
    const waist = reading(form.waist.value);
    const hips = reading(form.hips.value);
    const r = suggestSize(bust, waist, hips);
    if (!r.size) {
      const bad = ['bust', 'waist', 'hips'].filter((k) => outOfRange(form[k].value));
      const message = bad.length
        ? 'Those measurements are in centimetres, between ' + CM_MIN + ' and ' + CM_MAX + '. Check the ' + listOf(bad) + ' and try again.'
        : 'Pop in your ' + r.key + ' and we can suggest a size.';
      out.innerHTML = '<div class="stl-faout"><p style="margin:0">' + escapeHTML(message) + '</p></div>';
      ['bust', 'waist', 'hips'].forEach((k) => {
        if (bad.indexOf(k) !== -1) form[k].setAttribute('aria-invalid', 'true');
        else form[k].removeAttribute('aria-invalid');
      });
      return;
    }
    ['bust', 'waist', 'hips'].forEach((k) => form[k].removeAttribute('aria-invalid'));
    const label = (state.colourway && state.colourway.label) || '';
    const inStock = r.record ? r.record.available : false;
    const others = ((state.style && state.style.colourways) || [])
      .filter((c) => !c.current && c.sizes.some((z) => z.value === r.size && z.available))
      .map((c) => c.label || c.title);
    let stockLine = '';
    if (r.record) {
      if (inStock) stockLine = r.size + ' is in stock' + (label ? ' in ' + label : '') + '.';
      else if (others.length) stockLine = r.size + ' is sold out' + (label ? ' in ' + label : '') + ', but in stock in ' + listOf(others) + '.';
      else stockLine = r.size + ' is sold out' + (label ? ' in ' + label : '') + '.';
    }
    out.innerHTML =
      '<div class="stl-faout"><div class="stl-fa-size stl-serif">Size ' + escapeHTML(r.size) + '</div>' +
      '<p>' + escapeHTML(r.note) + '</p>' +
      (stockLine ? '<p>' + escapeHTML(stockLine) + '</p>' : '') +
      (r.record ? '<button type="button" class="stl-btn stl-btn-secondary" id="stl-faUse">Select ' + escapeHTML(r.size) + '</button>' : '') +
      '</div>';
    const use = $('#stl-faUse', out);
    if (use)
      use.addEventListener('click', () => {
        selectSize(r.record);
        core.closeModal();
      });
  };
  $('#stl-faGo', node).addEventListener('click', run);
  form.addEventListener('submit', (event) => {
    event.preventDefault();
    run();
  });
  form.addEventListener('keydown', (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      run();
    }
  });
  core.openModal(null, { title: words.title || 'Find my size', node, returnTo: trigger });
}

function selectSize(record) {
  if (!record) return;
  const input = $('variant-picker input[data-variant-id="' + record.id + '"]');
  if (input && !input.checked) {
    input.checked = true;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }
}

/* ---------------------------------------------------------------- 5. size guide and Afterpay triggers */
function conversionTable() {
  const words = readJSON($('[data-stl-pd-fit-words]')) || {};
  const rows = sizesOf()
    .map((z) => ({ say: z.value, au: convertAll(z.value, 'au'), us: convertAll(z.value, 'us'), eu: convertAll(z.value, 'eu') }))
    .filter((r) => r.au !== null);
  if (!rows.length) return '';
  return (
    '<h3 class="stl-guide-h">' + escapeHTML(words.conv_title || 'Size conversions') + '</h3>' +
    '<table><tr><th>Say size</th><th>AU</th><th>US</th><th>EU</th></tr>' +
    rows.map((r) => '<tr><td>' + escapeHTML(r.say) + '</td><td>' + r.au + '</td><td>' + r.us + '</td><td>' + r.eu + '</td></tr>').join('') +
    '</table>' +
    '<p class="stl-guide-note">' + escapeHTML(fillWords(words.conv_note, { anchor: state.cfg.sizeAnchor })) + '</p>'
  );
}

function convertAll(value, tab) {
  const parts = String(value).split(/\s*-\s*/).map((p) => convertOne(p.trim().toUpperCase(), tab));
  return parts.some((n) => n === null) ? null : parts.join('-');
}

/* A measurement page's body carries inline widths, stray meta tags and editor classes. Keep the
   words and the tables, drop the rest. */
function cleanGuide(fragment) {
  const box = document.createElement('div');
  box.appendChild(fragment.cloneNode(true));
  $$('meta, style, script, link, iframe, img[src=""]', box).forEach((el) => el.remove());
  $$('*', box).forEach((el) => {
    ['style', 'class', 'width', 'height', 'id'].forEach((a) => el.removeAttribute(a));
    Array.from(el.attributes).forEach((a) => {
      if (/^data-|^on/i.test(a.name)) el.removeAttribute(a.name);
    });
  });
  return box;
}

function openGuide(trigger) {
  const button = $('[data-stl-guide]');
  const opener = trigger || button;
  document.dispatchEvent(new CustomEvent('stl:size:open', { detail: { mode: 'guide', handle: state.cfg.handle } }));
  if (document.getElementById('stl-sizeModal')) {
    core.openOverlay('stl-sizeModal', { returnTo: opener });
    return;
  }
  /* fallback while the size modal section prints nothing: the shared modal, the style's own page */
  const src = $('template[data-stl-size-guide-src]');
  const node = document.createElement('div');
  node.className = 'stl-guide';
  if (src) {
    const cm = src.content.querySelector('[data-stl-size-guide="cm"]');
    if (cm) node.appendChild(cleanGuide(cm));
  }
  node.insertAdjacentHTML('beforeend', conversionTable());
  const title = (button && button.dataset.stlGuideTitle) || 'Size guide';
  core.openModal(null, { title, node, returnTo: opener });
}

function openAfterpay(trigger) {
  if (document.getElementById('stl-apDrawer')) {
    core.openDrawer('stl-apDrawer', { returnTo: trigger });
    return;
  }
  /* fallback while the Afterpay drawer section prints nothing: the shared drawer, the mock's words */
  const rows = [
    ['Sign up', 'Choose Afterpay as your payment at checkout. A debit or credit card is all you need to open an account.'],
    ['Pay in four', 'The first instalment is taken when you order, the other three every two weeks.'],
    ['Instant approval', 'You know within seconds, and your order ships exactly as it would with any other payment.'],
    ['Nothing extra to pay', 'The instalments are interest free. The only fees are for late payments, and Afterpay reminds you before each one is due.'],
  ];
  const html =
    rows.map((r) => '<p><b style="font-weight:500">' + r[0] + '</b><br>' + r[1] + '</p>').join('') +
    '<p class="stl-muted" style="font-size:13px">Afterpay purchases can be returned for a gift card rather than a refund. Available to Australian customers over 18.</p>';
  core.openDrawer(null, { title: 'Buy now, pay later', html, returnTo: trigger });
}

/* ---------------------------------------------------------------- 6. story bubbles */
function initStories(signal) {
  const bubbles = $$('[data-stl-story]');
  if (!bubbles.length) return;
  bubbles.forEach((b, i) => b.setAttribute('data-stl-story', String(i)));
  const calm = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const load = (video, src) => {
    if (video.dataset.stlLoaded) return;
    video.dataset.stlLoaded = '1';
    video.preload = 'metadata';
    video.src = src;
  };
  if ('IntersectionObserver' in window && !calm) {
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const video = $('[data-stl-story-video]', entry.target);
          if (!video) return;
          if (entry.isIntersecting) {
            load(video, entry.target.dataset.stlVideo);
            const p = video.play();
            if (p && p.catch) p.catch(() => {});
          } else if (video.dataset.stlLoaded) {
            video.pause();
          }
        });
      },
      { rootMargin: '0px', threshold: 0.25 }
    );
    bubbles.forEach((b) => io.observe(b));
    /* the editor throws these bubbles away: stop watching them with the rest of the wiring */
    if (signal) signal.addEventListener('abort', () => io.disconnect(), { once: true });
    document.addEventListener(
      'visibilitychange',
      () => {
        if (document.hidden) bubbles.forEach((b) => $('[data-stl-story-video]', b)?.pause());
      },
      { signal }
    );
  }
  delegate(
    document,
    'click',
    '[data-stl-story]',
    (event, button) => {
      /* the player is slice 4b's: it reads the clip off the button and opens #stl-player itself */
      const detail = { src: button.dataset.stlVideo, title: button.dataset.stlLabel || '', poster: button.dataset.stlPoster || '', trigger: button };
      document.dispatchEvent(new CustomEvent('stl:story:play', { detail }));
    },
    { signal }
  );
}

/* ---------------------------------------------------------------- 7. over photo one, and the pill */
/* The model line is a disclosure control: the markup carries role="button" and aria-expanded,
   and these two give it the id to point at and keep the state honest. The id is written here
   rather than in Liquid because the line is cloned onto each gallery host, and an id may not be. */
let modelBoxSeq = 0;

function wireModelBox(root) {
  $$('.stl-model', root).forEach((model) => {
    const box = $('.stl-mbox', model);
    const text = $('.stl-mtxt', model);
    if (!box || !text) return;
    if (!box.id) {
      modelBoxSeq += 1;
      box.id = 'stl-mbox-' + modelBoxSeq;
    }
    if (text.getAttribute('aria-controls') !== box.id) text.setAttribute('aria-controls', box.id);
    syncModelExpanded(model);
  });
}

function syncModelExpanded(model) {
  const box = $('.stl-mbox', model);
  const text = $('.stl-mtxt', model);
  if (!box || !text) return;
  const open = getComputedStyle(box).display !== 'none' ? 'true' : 'false';
  if (text.getAttribute('aria-expanded') !== open) text.setAttribute('aria-expanded', open);
}

function placeOverlay() {
  const source = $('[data-stl-pd-overlay]');
  if (!source || !source.children.length) return;
  const hosts = [$('media-gallery slideshow-slide'), $('media-gallery .media-gallery__grid > li')].filter(Boolean);
  let placed = false;
  hosts.forEach((host) => {
    if ($(':scope > .stl-pd-overlay-host', host)) return;
    placed = true;
    const layer = document.createElement('div');
    layer.className = 'stl-pd-overlay-host stl-scope';
    /* the slide carries the theme's on:click zoom; an on:click of our own stops a tap on the daisy
       or the model line from opening the zoom (the theme resolves the CLOSEST on:click only) */
    Array.from(source.children).forEach((child) => {
      const copy = child.cloneNode(true);
      copy.setAttribute('on:click', '/stlNoop');
      layer.appendChild(copy);
    });
    if (getComputedStyle(host).position === 'static') host.style.position = 'relative';
    host.appendChild(layer);
    wireModelBox(layer);
  });
  if (placed) wishlist.sync(document);
  const prompt = window.matchMedia && window.matchMedia('(hover: hover)').matches ? state.cfg.words.hover : state.cfg.words.tap;
  $$('[data-stl-model-prompt]').forEach((el) => {
    if (el.textContent !== prompt) el.textContent = prompt;
  });
}

function initModel(signal) {
  delegate(
    document,
    'click',
    '.stl-pd-overlay-host .stl-mtxt',
    (event, el) => {
      const model = el.closest('.stl-model');
      if (model && $('.stl-mbox', model)) {
        model.classList.toggle('stl-is-open');
        syncModelExpanded(model);
      }
    },
    { signal }
  );
  delegate(
    document,
    'keydown',
    '.stl-pd-overlay-host .stl-mtxt',
    (event, el) => {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      event.preventDefault();
      el.click();
    },
    { signal }
  );
  /* with a mouse the box also opens on hover and on focus (CSS :hover and :focus-within), so the
     state a screen reader reads has to follow both of those too, not only the click.
     mouseover/mouseout rather than mouseenter/mouseleave: those two do not bubble, so a delegate
     on the document would never see them. */
  ['mouseover', 'mouseout', 'focusin', 'focusout'].forEach((type) => {
    document.addEventListener(
      type,
      (event) => {
        const model = event.target instanceof Element && event.target.closest('.stl-model');
        if (!model) return;
        /* focusin and mouseover: the element is already focused or hovered, so :focus-within and
           :hover match now. focusout and mouseout: neither stops matching until the event has been
           dispatched, so read again on the next task. */
        syncModelExpanded(model);
        setTimeout(() => syncModelExpanded(model), 0);
      },
      { capture: true, signal }
    );
  });
  delegate(
    document,
    'click',
    '.stl-model [data-stl-u]',
    (event, button) => {
      const unit = button.dataset.stlU;
      const model = button.closest('.stl-model');
      $$('[data-stl-u]', model).forEach((b) => {
        const on = b === button;
        b.classList.toggle(ON, on);
        b.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
      $$('[data-stl-cm]', model).forEach((row) => {
        const cm = parseFloat(row.dataset.stlCm);
        row.textContent = row.dataset.stlLab + ': ' + (unit === 'in' ? (cm / 2.54).toFixed(1) + 'in' : cm + 'cm');
      });
    },
    { signal }
  );
  document.addEventListener(
    'click',
    (event) => {
      if (event.target instanceof Element && event.target.closest('.stl-model')) return;
      $$('.stl-model.stl-is-open').forEach((m) => {
        m.classList.remove('stl-is-open');
        syncModelExpanded(m);
      });
    },
    { signal }
  );
  /* the Size & fit accordion repeats the model as a sentence */
  const sentence = $('[data-stl-model-sentence]');
  const model = $('[data-stl-pd-overlay] [data-stl-model]');
  if (sentence && model) {
    const values = { name: model.dataset.stlModelName, size: model.dataset.stlModelSize, height: model.dataset.stlModelHeight || '' };
    const template = values.height ? sentence.dataset.stlWordsHeight : sentence.dataset.stlWords;
    sentence.textContent = fillWords(template, values);
    sentence.hidden = false;
  }
}

function ensureCount() {
  const gallery = $('media-gallery');
  if (!gallery) return;
  const slides = $$('slideshow-slide', gallery);
  const container = $('slideshow-container', gallery);
  if (slides.length < 2 || !container) return;
  let pill = $('.stl-gal-count', container);
  if (!pill) {
    pill = document.createElement('div');
    pill.className = 'stl-gal-count';
    pill.id = 'stl-galCount';
    pill.setAttribute('aria-hidden', 'true');
    if (getComputedStyle(container).position === 'static') container.style.position = 'relative';
    container.appendChild(pill);
  }
  const selected = $$('.slideshow-controls__thumbnail', gallery).findIndex((t) => t.getAttribute('aria-selected') === 'true');
  const text = Math.max(0, selected) + 1 + ' / ' + slides.length;
  if (pill.textContent !== text) pill.textContent = text;
}

function initGallery(signal) {
  placeOverlay();
  ensureCount();
  document.addEventListener(
    'slideshow:select',
    (event) => {
      if (!(event.target instanceof Element) || !event.target.closest('media-gallery')) return;
      const pill = $('media-gallery .stl-gal-count');
      const total = $$('media-gallery slideshow-slide').length;
      const index = event.detail && typeof event.detail.index === 'number' ? event.detail.index : 0;
      if (pill) pill.textContent = index + 1 + ' / ' + total;
    },
    { signal }
  );
  /* the theme rebuilds the gallery when a variant carries its own photo; put our pieces back */
  let queued = false;
  observe($('.product-information__media') || $('media-gallery'), { childList: true, subtree: true }, signal, () => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => {
      queued = false;
      placeOverlay();
      ensureCount();
    });
  });
}

/* ---------------------------------------------------------------- 8. sticky bars */
function initSticky(signal) {
  const bar = document.getElementById('stl-stickyAtc');
  const top = document.getElementById('stl-topbar');
  if (!bar && !top) return;
  const mainButton = () => $('[data-stl-atc-row] add-to-cart-component button') || $('add-to-cart-component button');
  const stopAt = () => $('.stl-section--product-related') || $('.shopify-section:has(product-recommendations)') || $('footer, .stl-section--footer');
  const headerBottom = () => {
    const header = $('.stl-hdr') || document.getElementById('header-component');
    if (!header) return 0;
    const pos = getComputedStyle(header).position;
    let sticky = pos === 'sticky' || pos === 'fixed';
    if (!sticky && header.parentElement) {
      const p = getComputedStyle(header.closest('#header-group, .shopify-section') || header.parentElement).position;
      sticky = p === 'sticky' || p === 'fixed';
    }
    if (!sticky) return 0;
    return Math.max(0, Math.round(header.getBoundingClientRect().bottom));
  };
  let ticking = false;
  const tick = () => {
    ticking = false;
    const button = mainButton();
    if (!button) return;
    const a = button.getBoundingClientRect();
    const stop = stopAt();
    const b = stop ? stop.getBoundingClientRect().top : Infinity;
    const wide = window.innerWidth >= 900;
    /* Phones (F82, 23 Oct 2026): the bar shows whenever the real button is out of view, above OR
       below, so Add to bag is on the first screen from load (the button sat 920-1,070px down on a
       ~664px phone screen). From 900px the top bar keeps the old rule: only once the button has
       scrolled off the top. 64 is the bar's own height, so the two never sit on top of each other. */
    const vh = window.innerHeight || document.documentElement.clientHeight;
    const show = (a.bottom < 0 || (!wide && a.top > vh - 64)) && b > 120;
    const set = (el, on) => {
      if (!el) return;
      if (el.classList.contains(ON) !== on) {
        el.classList.toggle(ON, on);
        el.setAttribute('aria-hidden', on ? 'false' : 'true');
        $$('button', el).forEach((btn) => (btn.tabIndex = on ? 0 : -1));
      }
    };
    set(bar, show && !wide);
    set(top, show && wide);
    if (top && show && wide) top.style.setProperty('--stl-topbar-top', headerBottom() + 'px');
    if (core.syncOverlays) core.syncOverlays();
  };
  const ask = () => {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(tick);
  };
  window.addEventListener('scroll', ask, { passive: true, signal });
  window.addEventListener('resize', ask, { passive: true, signal });
  ask();

  delegate(
    document,
    'click',
    '[data-stl-sticky-add]',
    (event, button) => {
      if (button.dataset.stlMode === 'wait') {
        openWaitlist();
        return;
      }
      /* one add at a time (audit T10, 29 Oct 2026): a second tap while the first is still on its
         way added a second unit. Busy from the tap until the cart says it changed (cart:update),
         or 1.5 s at most so the bar can never lock. A tap the size gate stops is not an add. */
      if (button.getAttribute('aria-busy') === 'true') return;
      const main = mainButton();
      if (!main || main.disabled) return;
      if (state.sizeChosen) {
        $$('[data-stl-sticky-add]').forEach((b) => b.setAttribute('aria-busy', 'true'));
        clearTimeout(stickyBusy);
        stickyBusy = setTimeout(clearStickyBusy, 1500);
      }
      main.click();
    },
    { signal }
  );
  document.addEventListener('cart:update', clearStickyBusy, { signal });
}

let stickyBusy = 0;
function clearStickyBusy() {
  clearTimeout(stickyBusy);
  $$('[data-stl-sticky-add][aria-busy]').forEach((b) => b.removeAttribute('aria-busy'));
}

/* ---------------------------------------------------------------- start */
/**
 * Every listener, observer and interval the product page owns, bound to one `.stl-pd-root` and
 * torn down with `signal`. Called once on load and again for each theme-editor re-render.
 */
function wireProduct(root, signal) {
  /* A counter on the node itself, so a reviewer can tell one bind from two: fresh markup reads
     "1", and anything that wired the SAME node twice would read "2". */
  root.setAttribute('data-stl-pd-wired', String(Number(root.getAttribute('data-stl-pd-wired') || 0) + 1));
  state.root = root;
  state.cfg = readJSON($('[data-stl-pd-config]', root)) || {};
  state.cfg.words = state.cfg.words || {};
  state.cfg.sizeAnchor = Number(state.cfg.sizeAnchor) || 6;
  state.cfg.lowStock = Number(state.cfg.lowStock) || 5;
  state.style = readJSON($('script[data-stl-style]', root));
  state.facts = readJSON($('script[data-stl-market-facts]', root));
  state.colourway = state.style ? (state.style.colourways || []).find((c) => c.current) || (state.style.colourways || [])[0] || null : null;
  state.variantId = currentVariantId();
  state.preHeldWrites = 0;

  initSizeGate(signal);
  paintPreGate();
  initTabs(signal);
  sortPlainOptions();
  paintStockLine();
  paintAfterpay();
  watchAddButton(signal);
  paintSticky();
  paintReassure();
  initGallery(signal);
  initModel(signal);
  initStories(signal);
  initSticky(signal);

  document.addEventListener(
    'variant:update',
    () => {
      /* the theme writes the new id into the form inside the same event; read it a frame later */
      requestAnimationFrame(onVariantChange);
    },
    { signal }
  );
  document.addEventListener('variant:selected', () => requestAnimationFrame(onVariantChange), { signal });

  delegate(document, 'click', '[data-stl-fit]', (event, button) => openFit(button), { signal });
  delegate(document, 'click', '[data-stl-guide]', (event, button) => openGuide(button), { signal });
  delegate(document, 'click', '[data-stl-open-guide]', (event, button) => openGuide(button), { signal });
  delegate(document, 'click', '[data-stl-ap-learn]', (event, button) => openAfterpay(button), { signal });
  delegate(document, 'click', '[data-stl-waitlist]', () => openWaitlist(), { signal });

  /* STOQ arrives late: repaint the stock line once its button is on the page */
  let tries = 0;
  const waitForStoq = setInterval(() => {
    tries += 1;
    if (stoqButton() || tries > 10) {
      clearInterval(waitForStoq);
      paintStockLine();
      paintSticky();
    }
  }, 800);
  if (signal) signal.addEventListener('abort', () => clearInterval(waitForStoq), { once: true });

  document.documentElement.setAttribute('data-stl-product', 'ready');
}

/* ------------------------------------------------------------------ the theme editor */
/* One AbortController per section wrapper. `shopify:section:load` carries the NEW wrapper as its
   target, so the key is the wrapper's id; on a normal page load it is the wrapper the buy box is
   already inside. `shopify:section:select` and `shopify:block:select` are not here on purpose:
   they do not replace any DOM, so nothing has come unbound when they fire. */
const wired = new Map();

function sectionKey(root) {
  return (root && root.id) || 'stl-product-document';
}

function wireSection(root) {
  if (!root || typeof root.querySelector !== 'function') return;
  const key = sectionKey(root);
  const previous = wired.get(key);
  if (previous) {
    previous.abort();
    wired.delete(key);
  }
  const pd = root.querySelector('.stl-pd-root');
  if (!pd) return;
  const controller = new AbortController();
  wired.set(key, controller);
  wireProduct(pd, controller.signal);
}

function unwireSection(root) {
  const key = sectionKey(root);
  const previous = wired.get(key);
  if (!previous) return;
  previous.abort();
  wired.delete(key);
}

onReady(() => {
  const pd = $('.stl-pd-root');
  if (pd) wireSection(pd.closest('[id^="shopify-section-"]') || document.documentElement);
  document.addEventListener('shopify:section:load', (event) => wireSection(event.target));
  document.addEventListener('shopify:section:unload', (event) => unwireSection(event.target));
});
